import {
  Box,
  GridItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Image,
  IconButton,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  Tooltip,
  Center,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Link } from "react-router-dom";
import { BlueButton } from "../Buttons";
import { CoursePreviewFragment } from "../fragments.generated";
import {
  useCopyCourseMutation,
  useDeleteCourseMutation,
  useUpdateCourseMutation,
} from "./index.generated";
import dummyImage from "./map.svg";
import { WarningIcon } from "@chakra-ui/icons";

export const CourseIcon = ({
  course,
  userGroupName,
  isActiveRouteChangeAlertFlag,
  isViewMode = true,
}: {
  course: CoursePreviewFragment;
  userGroupName?: string;
  isActiveRouteChangeAlertFlag: boolean | undefined;
  isViewMode?: boolean;
}) => {
  const link = isViewMode
    ? `/courses/view/${course.id}`
    : `/courses/${course.id}`;
  // add 9 hours
  const updatedAt = new Date(course.updatedAt);
  updatedAt.setHours(updatedAt.getHours() + 9);
  // format like YYYY-MM-DD HH:mm
  const updatedAtStr =
    updatedAt.toLocaleDateString() +
    " " +
    updatedAt.toLocaleTimeString().slice(0, -3);
  // 画像のキャッシュを止めるために、ランダムな文字列をURLに付与する。
  const randomStr = Math.random().toString(32).substring(2);

  const imageURL = `https://sbrd-maker-be-prd-course-public-images.s3.ap-northeast-1.amazonaws.com/thumbnail/${course.id}.svg?random=${randomStr}`;

  return (
    <GridItem w="full" overflow={"hidden"}>
      <Link to={link} key={course.id}>
        <Tooltip
          hasArrow
          label={course.name}
          bgColor={"white"}
          textColor={"#16181a"}
        >
          <Box
            justifyContent={"center"}
            border="1px"
            borderColor="#4880C8"
            position="relative"
          >
            <Image src={imageURL} w="full" fallbackSrc={dummyImage} />

            {isActiveRouteChangeAlertFlag && course.routeChangeAlert && (
              <Box
                w="full"
                h="40px"
                position="absolute"
                bottom="0"
                left="0"
                bg="#CC3D49"
              >
                <Center h="full">
                  <Text fontSize="16px" color="#FFFFFF" fontWeight="bold">
                    経路を修正してください
                  </Text>
                </Center>
              </Box>
            )}
          </Box>
        </Tooltip>
      </Link>
      <Flex justifyContent={"space-between"}>
        <Box w="80%">
          <CourseNamePreview name={course.name} />
        </Box>
        {!isViewMode ? (
          <Box>
            <CourseMenu id={course.id} name={course.name} />
          </Box>
        ) : null}
      </Flex>
      <Flex w="full" direction={"column"}>
        <Flex w="full" direction={"row-reverse"}>
          <Text fontSize={12} color="white" fontWeight="bold">
            {updatedAtStr}
          </Text>
        </Flex>
        <Flex w="full" direction={"row-reverse"}>
          <Text fontSize={12} color="white" fontWeight="bold">
            by{" "}
            {course.updatedBy.length > 13
              ? course.updatedBy.substring(0, 12) + "..."
              : course.updatedBy}
          </Text>
        </Flex>
        <Flex w="full" direction={"row-reverse"}>
          <Text
            right={2}
            bottom={1}
            color="white"
            fontSize={12}
            fontWeight={"bold"}
          >
            {userGroupName}
          </Text>
        </Flex>
        <Flex w="full" direction={"row-reverse"}>
          {import.meta.env.VITE_FEATURE_FLAG_ENABLE_ASSET_EDITOR == "true" && (
            <Text
              right={2}
              bottom={1}
              color="white"
              fontSize={12}
              fontWeight={"bold"}
            >
              コースID: {course.assetId}
            </Text>
          )}
        </Flex>
      </Flex>
    </GridItem>
  );
};

const CourseNamePreview = ({ name }: { name: string }) => {
  // const previewName = name.length > 13 ? name.substring(0, 12) + "..." : name;

  return (
    <Text
      color="white"
      fontWeight="bold"
      fontSize={20}
      mt={2}
      whiteSpace="nowrap"
      overflow={"hidden"}
    >
      {name}
    </Text>
  );
};

const CourseMenu = ({ id, name }: { id: string; name: string }) => {
  return (
    <Menu>
      <MenuButton
        zIndex={100}
        mt={1}
        as={IconButton}
        bgColor="#16181a"
        _hover={{ bgColor: "#16181a" }}
        _active={{ bgColor: "#16181a" }}
      >
        <BsThreeDotsVertical color="white" size={20} />
      </MenuButton>
      <MenuList>
        <DeleteCourseMenu id={id} name={name} />
        <UpdateCourseMenu id={id} name={name} />
        <CopyCourseMenu id={id} name={name} />
      </MenuList>
    </Menu>
  );
};

const DeleteCourseMenu = ({ id, name }: { id: string; name: string }) => {
  const toast = useToast();
  const [deleteCourseRes, deleteCourse] = useDeleteCourseMutation();

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (deleteCourseRes.data) {
      toast({
        title: "コースの削除に成功しました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [deleteCourseRes.data]);
  return (
    <>
      <MenuItem textColor={"#16181a"} onClick={onOpen}>
        コースを削除
      </MenuItem>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>コースの削除</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text pb={2} fontSize={20}>
              下記コースを削除します。{<br />}
              この操作は取り消せません。
            </Text>
            <Text pb={2} fontSize={20}>
              コース名 : {name}
            </Text>
          </ModalBody>

          <ModalFooter>
            <Flex>
              <BlueButton
                onClick={() => {
                  deleteCourse({ input: { id } });
                  onClose();
                }}
                label={"削除"}
              />
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const UpdateCourseMenu = ({ id, name }: { id: string; name: string }) => {
  const toast = useToast();

  const [updateCourseRes, updateCourse] = useUpdateCourseMutation();

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (updateCourseRes.data) {
      toast({
        title: "コース名の変更に成功しました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [updateCourseRes.data]);

  const [newName, setNewName] = useState(name);
  return (
    <>
      <MenuItem textColor={"#16181a"} onClick={onOpen}>
        コース名の編集
      </MenuItem>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>コースの編集</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text pb={2} fontSize={20}>
              新しいコース名を入力してください。
            </Text>
            <Text pb={2} fontSize={20}>
              元のコース名 : {name}
            </Text>
            <Input
              placeholder={name}
              onChange={(event) => {
                setNewName(event.target.value);
              }}
            ></Input>
          </ModalBody>

          <ModalFooter>
            <Flex>
              <BlueButton
                onClick={() => {
                  if (newName.replaceAll(" ", "") === "") {
                    toast({
                      title: "コース名を入力してください。",
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                    return;
                  }
                  updateCourse({ input: { id, name: newName } });
                  onClose();
                }}
                label={"更新"}
              />
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const CopyCourseMenu = ({ id, name }: { id: string; name: string }) => {
  const toast = useToast();
  const [copyCourseRes, copyCourse] = useCopyCourseMutation();

  useEffect(() => {
    if (copyCourseRes.data) {
      toast({
        title: "コースのコピーに成功しました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [copyCourseRes.data]);

  return (
    <>
      <MenuItem
        textColor={"#16181a"}
        onClick={() => {
          copyCourse({
            input: { sourceCourseId: id, newCourseName: name + "_コピー" },
          });
        }}
      >
        コースのコピー
      </MenuItem>
    </>
  );
};
